import { useCallback, useMemo, useState } from 'react';
import { RenderIf } from 'react-rainbow-components';
import { query, where } from 'firebase/firestore';
import useTodoCollection from 'data/firestore/agent/todo/useCollection';
import useManageTodos from 'data/hooks/useManageTodos';
import Tasks from 'components/icons/tasks';
import TodoList from './TodoList';
import TodoCreateForm from './TodoCreateForm';
import {
    TodosContainer,
    EmptyState,
    EmptyStateTitle,
    EmptyStateDescription,
} from './styled';

interface TodosTabProps {
    agentId: string;
    opportunityId?: string;
    pipelineId?: string;
    stageId?: string;
}

interface TodoUpdatePayload {
    title?: string;
    description?: string;
    status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
    priority?: 'low' | 'medium' | 'high';
    assignedTo?: any | null;
    dueDate?: string | null;
    metadata?: Record<string, unknown>;
}

const TodosTab = ({
    agentId,
    opportunityId,
    pipelineId,
    stageId,
}: TodosTabProps) => {
    const [loadingTodoIds, setLoadingTodoIds] = useState<Set<string>>(new Set());
    const { data: todos, isLoading } = useTodoCollection(agentId, {
        listQueryFn: (ref) => query(
            ref,
            where('opportunityId', '==', opportunityId),
            where('status', '!=', 'cancelled'),
        ),
        disabled: !agentId || !opportunityId,
        track: [agentId, opportunityId],
    });

    const { updateTodo, cancelTodo } = useManageTodos({
        agentId,
    });

    const handleStatusToggle = useCallback(
        async (todoId: string, newStatus: string) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await updateTodo(todoId, { status: newStatus as any });
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [updateTodo],
    );

    const handleCancel = useCallback(
        async (todoId: string) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await cancelTodo(todoId);
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [cancelTodo],
    );

    const handleUpdate = useCallback(
        async (todoId: string, payload: TodoUpdatePayload) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await updateTodo(todoId, payload);
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [updateTodo],
    );

    const hasTodos = useMemo(() => todos.length > 0, [todos]);

    const canCreateTodo = Boolean(opportunityId && pipelineId && stageId);

    return (
        <TodosContainer>
            <RenderIf isTrue={!isLoading && !hasTodos}>
                <EmptyState>
                    <Tasks />
                    <EmptyStateTitle>No TODOs yet</EmptyStateTitle>
                    <EmptyStateDescription>
                        TODOs for this opportunity will appear here
                    </EmptyStateDescription>
                </EmptyState>
            </RenderIf>

            <RenderIf isTrue={canCreateTodo}>
                <TodoCreateForm
                    agentId={agentId}
                    opportunityId={opportunityId!}
                    pipelineId={pipelineId!}
                    stageId={stageId!}
                />
            </RenderIf>

            <RenderIf isTrue={hasTodos}>
                <TodoList
                    todos={todos}
                    onStatusToggle={handleStatusToggle}
                    onCancel={handleCancel}
                    onUpdate={handleUpdate}
                    loadingTodoIds={loadingTodoIds}
                    agentId={agentId}
                />
            </RenderIf>
        </TodosContainer>
    );
};

export default TodosTab;
